import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface ConfirmationDialogProps {
  isOpen?: boolean;
  open?: boolean;
  onClose: () => void;
  onOpenChange?: (open: boolean) => void;
  title: string;
  subtitle?: string;
  description?: string;
  message?: React.ReactNode;
  children?: React.ReactNode;
  onConfirm: () => void | Promise<void>;
  confirmText?: string;
  cancelText?: string;
  isPending?: boolean;
  loading?: boolean;
  confirmVariant?:
    | "primary"
    | "danger"
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost";
  icon?: React.ReactNode;
  className?: string;
}

function getButtonVariant(
  variant: ConfirmationDialogProps["confirmVariant"]
): "default" | "outline" | "secondary" | "ghost" | "destructive" | "link" {
  switch (variant) {
    case "danger":
    case "destructive":
      return "destructive";
    case "secondary":
      return "secondary";
    case "outline":
      return "outline";
    case "ghost":
      return "ghost";
    case "primary":
    case "default":
    default:
      return "default";
  }
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  open,
  onClose,
  onOpenChange,
  title,
  subtitle,
  description,
  message,
  children,
  onConfirm,
  confirmText = "Confirm",
  cancelText = "Cancel",
  isPending = false,
  loading = false,
  confirmVariant = "primary",
  icon,
  className,
}) => {
  const isDialogOpen = open ?? isOpen ?? false;
  const isLoading = isPending || loading;
  const subText = subtitle || description;
  const bodyContent = message ?? children;

  const handleOpenChange = (newOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(newOpen);
    }
    if (!newOpen && onClose) {
      onClose();
    }
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
      <DialogContent className={cn("sm:max-w-md", className)}>
        <DialogHeader>
          <div className="flex items-start gap-3">
            {icon && <div className="mt-0.5 shrink-0">{icon}</div>}
            <div className="space-y-1">
              <DialogTitle>{title}</DialogTitle>
              {subText && (
                <DialogDescription>{subText}</DialogDescription>
              )}
            </div>
          </div>
        </DialogHeader>

        {bodyContent && (
          <div className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">
            {typeof bodyContent === "string" ? (
              <p className="leading-relaxed">{bodyContent}</p>
            ) : (
              bodyContent
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelText}
          </Button>
          <Button
            type="button"
            variant={getButtonVariant(confirmVariant)}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? "Processing..." : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ConfirmationModal = ConfirmationDialog;
export default ConfirmationDialog;
